import { useGetClientByIdQuery } from '@entities/clients/rtk-query.ts';
import { useGroups } from '@entities/common/groups';
import { type FC, useEffect, useMemo } from 'react';
import { Controller, FormProvider } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { contactIconsMap, type ContactType } from '@widgets/contacts/contact-edit/constans.tsx';
import { useDescriptionEditor } from '@widgets/shared/descriptionEditor/model.ts';
import { ContactsArrayEditor } from '@widgets/shared/Forms/ContactsArrayEditor/index.ts';
import { FormDescriptionEditor } from '@widgets/shared/Forms/DescriptionEditor/index.tsx';
import { useTempClientFormPageRedirect } from '@features/clients/client-form/ui/form/helpers.ts';
import { ArrowShortLeft } from '@shared/assets';
import { useUser } from '@shared/hooks';
import { useSaveScroll } from '@shared/hooks/use-save-scroll.ts';
import * as shared from '@shared/styles/sharedStyles.module.scss';
import { Card, IconButton, ListBody, PageHead, PageTitle, TextButton, TextInputM } from '@shared/ui';
import { FormFieldWithLabel } from '@shared/ui/forms/FieldWithLabel/index.ts';
import { ImageInputCropper } from '@shared/ui/inputs/image-input-cropper';
import { TextOverflow } from '@shared/ui/TextOverflow/index.tsx';
import { Loader } from '@src/library/components/common/index.ts';
import { type IClientForm, useClientForm } from '../../model';
import { Birthday } from '../birthday';
import { Email } from '../email';
import { Gender } from '../gender';
import { GroupsField } from '../new-groups/groups-field';
import { Phone } from '../phone/';
import * as st from './styles.module.scss';

const _HASH = 'clientEditorDescriptionHash';
export const ClientForm: FC = () => {
  const { saveCurrentScrollPosition } = useSaveScroll('clientInListForm');
  const { clientId } = useParams();
  // const client = useClient(Number(clientId));
  const isNewClient = clientId === 'new';

  const { data: client } = useGetClientByIdQuery(Number(clientId), { skip: isNewClient });
  const { doRedirectOnPrevPage } = useTempClientFormPageRedirect();
  const isRegistered: boolean = client ? client?.is_registered : false;
  const { clearStorage: clearDescription } = useDescriptionEditor(_HASH);

  const user = useUser();

  const isCoach: boolean = user.role === 'coach';

  const onClose = () => {
    doRedirectOnPrevPage(`/clients/clients`);
    localStorage.removeItem('clientEditorFormState');
    // console.log('kek');
    clearDescription();
  };

  const { submit, isLoading, isClientLoading, ...methods } = useClientForm({
    selected: Number(clientId),
    onSuccess: onClose,
    skipQuery: isNewClient,
  });
  const clientPhoneNumber = methods.watch('phonenumber');
  const clientEmail = methods.watch('email');

  const { selectedGroups } = useGroups();

  const localFormSave: IClientForm | null = JSON.parse(
    localStorage.getItem('clientEditorFormState')!
  );

  useEffect(() => {
    if (localFormSave !== null) {
      // console.log('localFormSave !== null', localFormSave);
      localFormSave.last_name !== '' &&
        methods.setValue('last_name', localFormSave.last_name, {
          shouldValidate: true,
          shouldDirty: true,
        });
      localFormSave.first_name !== '' &&
        methods.setValue('first_name', localFormSave.first_name, {
          shouldValidate: true,
          shouldDirty: true,
        });
      localFormSave.middle_name !== '' &&
        methods.setValue('middle_name', localFormSave.middle_name, {
          shouldValidate: true,
          shouldDirty: true,
        });
      localFormSave.photo &&
        methods.setValue('photo', localFormSave.photo, {
          shouldValidate: true,
          shouldDirty: true,
        });
      localFormSave.active_contacts &&
        methods.setValue('active_contacts', localFormSave.active_contacts, {
          shouldValidate: true,
          shouldDirty: true,
        });
    }
  }, []);

  useEffect(() => {
    methods.setValue('custom_groups', selectedGroups, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [selectedGroups]);

  const fixedContacts = useMemo(() => {
    const output = [];
    if (clientPhoneNumber) {
      output.push({ type: 'phone', value: clientPhoneNumber });
    }
    if (clientEmail) {
      output.push({
        type: 'email',
        value: clientEmail,
      });
    }
    return output;
  }, [clientPhoneNumber, clientEmail]);

  const onRedirect = () => {
    saveCurrentScrollPosition();
    localStorage.setItem('clientEditorFormState', JSON.stringify(methods.getValues()));
  };

  return (
    <>
      <PageTitle title={`${clientId !== 'new' ? 'Редактирование' : 'Создание'} клиента`} />
      <PageHead
        Left={
          <IconButton variant="dark" onClick={onClose}>
            <ArrowShortLeft />
          </IconButton>
        }
        Right={
          <TextButton type="submit" onClick={methods.handleSubmit(submit)}>
            Готово
          </TextButton>
        }
      />
      <ListBody>
        {isClientLoading ? (
          <Loader />
        ) : (
          <FormProvider {...methods}>
            <form className={shared.forFormsGap20} onSubmit={methods.handleSubmit(submit)}>
              <section className={st.body}>
                <Card className={st.block_fio_foto}>
                  <Controller
                    name={'photo'}
                    control={methods.control}
                    disabled={true}
                    render={({ field: { onChange, value } }) => (
                      <ImageInputCropper
                        aspect={[1, 1]}
                        iconVariant="client"
                        value={value as string}
                        disabled={client?.is_registered}
                        onChange={(value) => {
                          !client?.is_registered &&
                            methods.setValue('photo', value, { shouldDirty: true });
                        }}
                        isValid={!!methods.formState.errors['photo']}
                      />
                    )}
                  />
                  <div className={st.fio}>
                    <TextInputM
                      firstLetterUppercase
                      fullWidth
                      autoFocus={false}
                      register={methods.register('last_name')}
                      error={methods.formState?.errors?.last_name?.message}
                      placeholder={'Фамилия'}
                      disabled={
                        // isLoading ||
                        isCoach && isRegistered
                      }
                    />
                    <TextInputM
                      fullWidth
                      firstLetterUppercase
                      autoFocus={false}
                      register={methods.register('first_name')}
                      error={methods.formState?.errors?.first_name?.message}
                      placeholder={'Имя'}
                      disabled={
                        // isLoading ||
                        isCoach && isRegistered
                      }
                    />
                    <TextInputM
                      fullWidth
                      firstLetterUppercase
                      autoFocus={false}
                      register={methods.register('middle_name')}
                      error={methods.formState?.errors?.last_name?.message}
                      placeholder={'Отчество'}
                      disabled={
                        // isLoading ||
                        isCoach && isRegistered
                      }
                    />
                  </div>
                </Card>

                {(isNewClient || !isRegistered) && (
                  <>
                    <Phone disabled={isCoach && isRegistered} />

                    <Email disabled={isCoach && isRegistered} />
                  </>
                )}

                <Gender
                  disabled={isCoach && isRegistered}
                  // isLoading={isLoading}
                />

                <Birthday
                  disabled={isCoach && isRegistered}
                  // isLoading={isLoading}
                />

                <FormDescriptionEditor
                  hash={_HASH}
                  fieldLabel={'О КЛИЕНТЕ'}
                  descriptionPageTitle={'О клиенте'}
                  onChange={(v) => methods.setValue('description', v)}
                  onRedirect={onRedirect}
                  initData={client?.description}
                />

                <GroupsField onRedirect={onRedirect} />

                {isRegistered && fixedContacts.length && (
                  <FormFieldWithLabel label={'КОНТАКТЫ'}>
                    {fixedContacts.map((contact) => (
                      <Card key={contact.value} className={st.mainContact}>
                        {contactIconsMap[contact.type as ContactType]}
                        <TextOverflow className={st.mainContactValue}>{contact.value}</TextOverflow>
                      </Card>
                    ))}
                  </FormFieldWithLabel>
                )}

                <ContactsArrayEditor
                  label={'ДОПОЛНИТЕЛЬНЫЕ КОНТАКТЫ'}
                  name={'active_contacts'}
                  setValue={methods.setValue}
                  control={methods.control}
                />
              </section>
            </form>
          </FormProvider>
        )}
      </ListBody>
    </>
  );
};
